// Domain constants, enums, and state-transition tables shared by the API and web.
// These are the single source of truth for status machines, scopes, and the
// policy-driven image and IP-risk mappings described in PLAN.md.
/* ------------------------------------------------------------------ *
 * Status enumerations
 * ------------------------------------------------------------------ */
export const RESEARCH_STATUSES = ['draft', 'reviewed', 'rejected'];
export const CONCEPT_STATUSES = [
    'draft',
    'needs_review',
    'approved',
    'rejected',
    'archived',
];
export const IMAGE_REQUEST_STATUSES = [
    'draft',
    'submitted_for_review',
    'approved',
    'rejected',
    'generating',
    'completed',
    'failed',
];
export const ASSET_REVIEW_STATUSES = ['pending', 'reviewed', 'rejected'];
export const INGESTED_IMAGE_STATUSES = ['uploaded', 'normalized', 'approved', 'rejected'];
export const NORMALIZATION_METHODS = ['checkerboard', 'solid_edge', 'both'];
/**
 * Lifecycle of a single node in an artwork lineage. Every node is one concrete,
 * content-addressed file. `pending` nodes await an explicit owner design review;
 * `approved`/`rejected` are terminal design decisions. Technical transparency
 * approval is tracked separately on the node analysis — a valid alpha channel
 * alone never implies design or print readiness.
 */
export const ARTWORK_ASSET_STATUSES = ['pending', 'approved', 'rejected'];
export const RESEARCH_TRANSITIONS = {
    draft: ['reviewed', 'rejected'],
    reviewed: [],
    rejected: [],
};
export const CONCEPT_TRANSITIONS = {
    draft: ['needs_review', 'archived'],
    needs_review: ['approved', 'rejected', 'archived'],
    approved: ['archived'],
    rejected: ['archived'],
    archived: [],
};
export const IMAGE_REQUEST_TRANSITIONS = {
    draft: ['submitted_for_review'],
    submitted_for_review: ['approved', 'rejected'],
    approved: ['generating'],
    rejected: [],
    generating: ['completed', 'failed'],
    completed: [],
    failed: [],
};
export const ASSET_REVIEW_TRANSITIONS = {
    pending: ['reviewed', 'rejected'],
    reviewed: [],
    rejected: [],
};
export const INGESTED_IMAGE_TRANSITIONS = {
    uploaded: ['normalized', 'approved', 'rejected'],
    normalized: ['normalized', 'approved', 'rejected'],
    approved: [],
    rejected: [],
};
export const ARTWORK_ASSET_TRANSITIONS = {
    pending: ['approved', 'rejected'],
    approved: [],
    rejected: [],
};
/** Returns true only when `to` is an explicitly permitted next state from `from`. */
export function canTransition(table, from, to) {
    return table[from]?.includes(to) ?? false;
}
/* ------------------------------------------------------------------ *
 * API key scopes
 *
 * Only draft-creating and read scopes are grantable to agent keys. Image
 * approval/generation, publishing, and credential management are never
 * exposed as scopes — those actions require an authenticated owner session.
 * ------------------------------------------------------------------ */
export const AGENT_SCOPES = [
    'research:read',
    'research:write',
    'concept:read',
    'concept:write',
    'prompt:read',
    'prompt:write',
];
/** Scopes granted to the initial agent key: draft creation plus reads only. */
export const DEFAULT_AGENT_SCOPES = AGENT_SCOPES;
/* ------------------------------------------------------------------ *
 * Image generation mappings (must match the Hermes OpenAI plugin)
 * ------------------------------------------------------------------ */
export const IMAGE_TIERS = ['low', 'medium', 'high'];
export const IMAGE_ASPECTS = ['square', 'landscape', 'portrait'];
export const ASPECT_TO_SIZE = {
    square: '1024x1024',
    landscape: '1536x1024',
    portrait: '1024x1536',
};
export const TIER_TO_QUALITY = {
    low: 'low',
    medium: 'medium',
    high: 'high',
};
/** The only image model the hub is permitted to call. */
export const IMAGE_MODEL = 'gpt-image-2';
/* ------------------------------------------------------------------ *
 * IP / policy risk triage
 * ------------------------------------------------------------------ */
export const IP_RISK_LEVELS = ['green', 'yellow', 'red'];
/**
 * Tokens that indicate a likely trademark, franchise, or protected-likeness
 * problem. Presence forces a `red` triage — a concept carrying red risk can
 * never be approved. This list is intentionally illustrative, not exhaustive;
 * human review remains mandatory.
 */
export const RED_IP_TOKENS = [
    'disney',
    'marvel',
    'pixar',
    'star wars',
    'harry potter',
    'pokemon',
    'nintendo',
    'nike',
    'adidas',
    'coca-cola',
    'nfl',
    'nba',
    'taylor swift',
    'mickey mouse',
    'super mario',
];
/**
 * Tokens that warrant caution and a documented owner resolution note before a
 * concept may proceed (style imitation, vintage/brand-adjacent framing, etc.).
 */
export const YELLOW_IP_TOKENS = [
    'inspired by',
    'style of',
    'in the style of',
    'tribute',
    'vintage',
    'retro',
    'parody',
    'lookalike',
    'fan art',
];
/**
 * Classifies free text into an IP-risk level using the token lists above.
 * Red dominates yellow, yellow dominates green. This is a triage aid; a human
 * reviewer makes the final call and "green" never means legal clearance.
 */
export function scanIpRisk(text) {
    const lower = text.toLowerCase();
    if (RED_IP_TOKENS.some((token) => lower.includes(token))) {
        return 'red';
    }
    if (YELLOW_IP_TOKENS.some((token) => lower.includes(token))) {
        return 'yellow';
    }
    return 'green';
}
/* ------------------------------------------------------------------ *
 * Research / concept vocabularies
 * ------------------------------------------------------------------ */
/**
 * Tokens forbidden in image prompts: trademarks/franchises (reused from the red
 * list) plus phrases that request imitation, logos, copied layouts, or protected
 * likenesses. A prompt containing any of these is rejected outright.
 */
export const PROMPT_FORBIDDEN_TOKENS = [
    ...RED_IP_TOKENS,
    'logo',
    'trademark',
    'brand name',
    'celebrity',
    'signature style',
    'in the style of',
    'exact replica',
    'copyrighted',
    'copyright',
    'lyrics',
    'famous painting',
    'movie poster',
    'album cover',
];
/** Returns the list of forbidden tokens present in the given prompt text. */
export function findPromptViolations(text) {
    const lower = text.toLowerCase();
    return PROMPT_FORBIDDEN_TOKENS.filter((token) => lower.includes(token));
}
export const SIGNAL_TYPES = [
    'demand',
    'occasion',
    'product_format',
    'price_band',
    'seasonality',
    'saturation',
    'trend',
];
export const PRODUCT_FORMATS = [
    'tshirt',
    'hoodie',
    'mug',
    'poster',
    'sticker',
    'tote_bag',
    'phone_case',
    'canvas',
    'hat',
    'notebook',
];
export const PRICE_BANDS = ['budget', 'mid', 'premium', 'luxury'];
export const SEASONALITY = ['none', 'spring', 'summer', 'fall', 'winter', 'holiday'];
export const FEASIBILITY_STATUSES = ['unknown', 'feasible', 'challenging', 'infeasible'];
export const SIGNAL_LEVELS = ['low', 'medium', 'high'];
/* ------------------------------------------------------------------ *
 * Actor types (for audit events)
 * ------------------------------------------------------------------ */
export const ACTOR_TYPES = ['owner', 'agent', 'system'];
/* ------------------------------------------------------------------ *
 * Artwork provenance & lineage
 *
 * An artwork lineage is a tree of content-addressed files rooted at an
 * immutable `original`. Every derivative names its parent and records how it
 * was derived, so the chain concept → prompt → original → derivatives →
 * rendered preview → production asset is fully traceable.
 * ------------------------------------------------------------------ */
export const ASSET_ROLES = [
    'original',
    'normalized',
    'externally_edited',
    'rendered_preview',
    'production',
];
/** How a derivative was produced from its parent. `upload` marks a root original. */
export const DERIVATION_TYPES = [
    'upload',
    'normalize',
    'external_edit',
    'render',
    'promote',
];
/** Roles a newly uploaded (as opposed to server-derived) file may take. */
export const UPLOADABLE_ASSET_ROLES = ['original', 'externally_edited'];
/** Provider that produced an externally generated original. */
export const EXTERNAL_GEN_PROVIDERS = ['chatgpt', 'openai', 'other', 'unknown'];
/** How an external original was generated. */
export const EXTERNAL_GEN_METHODS = ['manual_chat_ui', 'native_api', 'other', 'unknown'];
/* ------------------------------------------------------------------ *
 * Personalization definitions
 *
 * A personalization definition is a versioned, concept-scoped set of editable
 * text fields (e.g. the Book Club Mug's club/member/year lines). Customer input
 * is always treated as data — never markup or template code.
 * ------------------------------------------------------------------ */
export const PERSONALIZATION_INPUT_TYPES = ['single_line_text', 'year'];
/** Character allow-lists applied to customer input after normalization. */
export const ALLOWED_CHARACTER_POLICIES = [
    'printable_basic',
    'alphanumeric_spaces',
    'digits',
];
export const NORMALIZATION_POLICIES = ['none', 'trim', 'trim_collapse'];
export const PERSONALIZATION_DEF_STATUSES = ['draft', 'approved', 'archived'];
export const PERSONALIZATION_DEF_TRANSITIONS = {
    draft: ['approved', 'archived'],
    approved: ['archived'],
    archived: [],
};
const CHARACTER_POLICY_PATTERNS = {
    // Any character except HTML angle brackets (control chars rejected separately).
    printable_basic: /^[^<>]*$/u,
    // Letters, numbers, spaces, and a small set of safe punctuation.
    alphanumeric_spaces: /^[\p{L}\p{N} .,'&-]*$/u,
    digits: /^\d*$/,
};
/** True if the string contains ASCII control characters (0x00-0x1F or 0x7F). */
function hasControlChars(s) {
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 0x20 || c === 0x7f)
            return true;
    }
    return false;
}
/** Applies a field's normalization policy to a raw value. */
export function normalizeFieldValue(value, policy) {
    switch (policy) {
        case 'none':
            return value;
        case 'trim':
            return value.trim();
        case 'trim_collapse':
            return value.trim().replace(/\s+/g, ' ');
    }
}
/**
 * Normalizes and validates a single customer-entered value against a field's
 * rules. Returns the normalized value (or its fallback when empty and optional)
 * plus a list of human-readable errors. Never throws — callers decide how to
 * surface failures. The value is treated strictly as data.
 */
export function validatePersonalizationValue(field, rawValue) {
    const errors = [];
    const normalized = normalizeFieldValue(rawValue ?? '', field.normalizationPolicy);
    if (normalized === '') {
        if (field.required)
            errors.push('This field is required.');
        return { value: field.required ? '' : field.fallbackValue, errors };
    }
    if (normalized.length < field.minLength) {
        errors.push(`Must be at least ${field.minLength} characters.`);
    }
    if (normalized.length > field.maxLength) {
        errors.push(`Must be at most ${field.maxLength} characters.`);
    }
    if (field.inputType === 'year' && !/^\d{4}$/.test(normalized)) {
        errors.push('Must be a 4-digit year.');
    }
    if (hasControlChars(normalized) ||
        !CHARACTER_POLICY_PATTERNS[field.allowedCharacterPolicy].test(normalized)) {
        errors.push('Contains characters that are not allowed.');
    }
    return { value: normalized, errors };
}
/* ------------------------------------------------------------------ *
 * Text layouts
 *
 * A text layout is a versioned set of layers that place personalization fields
 * onto base artwork. Layers reference a bundled font by id and declare an
 * intentional overflow behavior; the render service applies them deterministically.
 * ------------------------------------------------------------------ */
export const HORIZONTAL_ALIGNMENTS = ['left', 'center', 'right'];
export const VERTICAL_ALIGNMENTS = ['top', 'middle', 'bottom'];
/** Deliberate handling when text exceeds its box; chosen per layer. */
export const OVERFLOW_BEHAVIORS = ['reject', 'shrink_to_fit', 'wrap', 'ellipsize'];
export const TEXT_LAYOUT_STATUSES = ['draft', 'approved', 'archived'];
export const TEXT_LAYOUT_TRANSITIONS = {
    draft: ['approved', 'archived'],
    approved: ['archived'],
    archived: [],
};
/**
 * Relative luminance (WCAG) of an #rrggbb color, 0 (black) to 1 (white).
 * Used to compute text/background contrast for print-QA.
 */
export function relativeLuminance(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m)
        return 0;
    const int = parseInt(m[1], 16);
    const srgb = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}
/** WCAG contrast ratio (1..21) between two #rrggbb colors. */
export function contrastRatio(fg, bg) {
    const l1 = relativeLuminance(fg);
    const l2 = relativeLuminance(bg);
    const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
    return (hi + 0.05) / (lo + 0.05);
}
/** Minimum contrast ratio required for personalization text to pass QA. */
export const MIN_TEXT_CONTRAST_RATIO = 4.5;
/**
 * Initial Book Club Mug personalization fields (issue #5 §D). Used to prefill
 * the definition editor; not auto-inserted. Values are validated by
 * `personalizationDefCreateSchema` before storage.
 */
export const BOOK_CLUB_PERSONALIZATION_FIELDS = [
    {
        fieldKey: 'clubName',
        label: 'Reading Circle / Book Club Name',
        inputType: 'single_line_text',
        required: true,
        minLength: 1,
        maxLength: 40,
        allowedCharacterPolicy: 'printable_basic',
        normalizationPolicy: 'trim_collapse',
        placeholder: 'Sunday Readers Circle',
        exampleValue: 'Sunday Readers Circle',
        sortOrder: 1,
    },
    {
        fieldKey: 'memberName',
        label: 'Member Name',
        inputType: 'single_line_text',
        required: true,
        minLength: 1,
        maxLength: 32,
        allowedCharacterPolicy: 'printable_basic',
        normalizationPolicy: 'trim_collapse',
        placeholder: 'Morgan',
        exampleValue: 'Morgan',
        sortOrder: 2,
    },
    {
        fieldKey: 'foundedYear',
        label: 'Founded Year',
        inputType: 'year',
        required: false,
        minLength: 4,
        maxLength: 4,
        allowedCharacterPolicy: 'digits',
        normalizationPolicy: 'trim',
        placeholder: '2026',
        exampleValue: '2026',
        fallbackValue: '',
        sortOrder: 3,
    },
];
/* ------------------------------------------------------------------ *
 * Product templates & print QA
 *
 * A product template binds a concept to a specific provider blueprint/version
 * with printable-area, safe-area, and handle-zone geometry. A production asset
 * is approved only against one explicit, approved template version and a
 * completed print-QA record.
 * ------------------------------------------------------------------ */
export const PRODUCT_PROVIDERS = ['printify', 'other'];
export const PRODUCT_TEMPLATE_STATUSES = ['draft', 'approved', 'archived'];
export const PRODUCT_TEMPLATE_TRANSITIONS = {
    draft: ['approved', 'archived'],
    approved: ['archived'],
    archived: [],
};
export const PRINT_QA_DECISIONS = ['pass', 'fail'];
/** True if inner box is fully contained within outer box. */
export function boxContains(outer, inner) {
    return (inner.x >= outer.x &&
        inner.y >= outer.y &&
        inner.x + inner.width <= outer.x + outer.width &&
        inner.y + inner.height <= outer.y + outer.height);
}
/** True if two boxes overlap (share any area). */
export function boxesOverlap(a, b) {
    return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}
/* ------------------------------------------------------------------ *
 * Field length / count limits (shared by API validation and UI hints)
 * ------------------------------------------------------------------ */
export const FIELD_LIMITS = {
    title: 200,
    summary: 2000,
    note: 2000,
    differentiation: 1000,
    consumerNeed: 1000,
    targetAudience: 500,
    prompt: 4000,
    constraint: 200,
    url: 2000,
    tag: 40,
    maxTags: 20,
    maxProductFormats: 10,
    maxResearchRefs: 25,
    maxNegativeConstraints: 30,
    filename: 255,
    editSummary: 500,
    generationRunReference: 200,
    uncertaintyNote: 500,
    modelName: 120,
};
//# sourceMappingURL=domain.js.map